/**
 * UAVTradeZone Modification Record
 *
 * Change ID:    UI_EXRATE_FILTER_001
 * Date:         2026-01-05
 * Modified By:  Cursor
 *
 * Description:
 * - Client: Filter FX list to only show USD, EUR, GBP per admin request.
 *
 * File Size Check:
 * - Current: ~147 lines
 * - Adding: ~12 lines
 * - Projected: ~159 lines
 * - Decision: Proceed
 */

import { useState, useEffect, useMemo, useRef } from "react";
import { listFx, FxRate } from "../../../services/fx";
import { Currency } from "../../../interfaces/Currency";
import { shallowEqual } from "../../../utils/shallowEqual";

interface FxListParams {
  page?: number;
  limit?: number;
  base?: Currency;
  quote?: Currency;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
  q?: string;
  minRate?: number;
  maxRate?: number;
}

interface FxListData {
  items: FxRate[];
  total: number;
  page: number;
  limit: number;
  pages: number;
}

interface UseFxListReturn {
  query: FxListParams;
  data: FxListData | null;
  loading: boolean;
  error: string | null;
  fetchList: (params?: Partial<FxListParams>) => void;
}

export const useFxList = (initialParams: FxListParams = {}): UseFxListReturn => {
  const [query, setQuery] = useState<FxListParams>({
    page: 1,
    limit: 50,
    sortBy: "base",
    sortOrder: "asc",
    ...initialParams,
  });

  const [data, setData] = useState<FxListData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Ref to track in-flight request and prevent duplicate calls
  const fetchRef = useRef<{ key?: string } | null>(null);

  // Memoize params to ensure stable dependency
  const params = useMemo(
    () => ({
      page: query.page,
      limit: query.limit,
      base: query.base,
      quote: query.quote,
      sortBy: query.sortBy,
      sortOrder: query.sortOrder,
      q: query.q,
      minRate: query.minRate,
      maxRate: query.maxRate,
    }),
    [
      query.page,
      query.limit,
      query.base,
      query.quote,
      query.sortBy,
      query.sortOrder,
      query.q,
      query.minRate,
      query.maxRate,
    ],
  );

  // Stable effect that depends only on params
  useEffect(() => {
    let cancelled = false;
    const ctrl = new AbortController();

    // Simple in-flight key to block duplicate calls for identical params
    const key = JSON.stringify(params);
    if (fetchRef.current?.key === key) {
      console.log("[useFxList] Skipping duplicate in-flight request");
      return;
    }
    fetchRef.current = { key };

    (async () => {
      try {
        setLoading(true);
        setError(null);

        const response = await listFx(params, { signal: ctrl.signal });

        if (!cancelled && response?.data?.success) {
          const responseData = response.data.data;
          // Only include allowed quotes: USD, EUR, GBP
          const allowed = new Set(["USD", "EUR", "GBP"]);
          const rows: FxRate[] = (responseData.rows || []).filter((r: FxRate) =>
            allowed.has((r.quote || "").toString().toUpperCase())
          );

          const totalFiltered = rows.length;
          const limit = responseData.limit || 50;
          const newData: FxListData = {
            items: rows,
            total: totalFiltered,
            page: responseData.page || 1,
            limit,
            pages: Math.ceil(totalFiltered / limit) || 1,
          };
          // Only update state if data actually changed
          setData((prev) => (shallowEqual(prev, newData) ? prev : newData));
        } else if (!cancelled) {
          throw new Error("Failed to fetch FX rates");
        }
      } catch (err: any) {
        if (!cancelled && err.name !== "CanceledError" && err.name !== "AbortError") {
          if (process.env.NODE_ENV !== "production") {
            console.warn("Error fetching FX rates:", err);
          }
          setError(err.response?.data?.error || err.message || "Failed to fetch FX rates");
          setData(null);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
          // Clear in-flight ref after request completes
          fetchRef.current = null;
        }
      }
    })();

    return () => {
      cancelled = true;
      ctrl.abort();
      // Clear in-flight ref on cleanup
      fetchRef.current = null;
    };
  }, [params]);

  // Manual fetch function for user-triggered updates
  const fetchList = (newParams?: Partial<FxListParams>) => {
    setQuery((prev) => ({ ...prev, ...newParams }));
  };

  return {
    query,
    data,
    loading,
    error,
    fetchList,
  };
};
